import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import dayjs from 'dayjs';
import { AttendanceApi, StudentAttendanceSummaryRow } from '@/services/school.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';
import { useDefaultYear } from './useDefaultYear';

const columnHelper = createColumnHelper<StudentAttendanceSummaryRow>();

export function AttendanceSummaryPage() {
  const term = useTerminology();
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'rollNumber', defaultSortOrder: 'asc' });
  const { years, yearId, setYearId, effectiveYearId, selectedYear, isLoading: yearLoading, hasAnyData } =
    useDefaultYear();

  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  // Default to the current month-to-date — not the whole academic year's
  // full date range (same bug and same fix as StudentAttendanceReportPage.tsx:
  // a school admin opening this report got a query spanning up to 12 months
  // by default, e.g. May 2026 to May 2027, when "this month's summary" is
  // what's actually useful day to day).
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));

  // Only re-derives the date range when the YEAR SELECTOR itself changes
  // (not on initial mount's current-month default above). If "this month"
  // falls inside the newly selected year, keep showing current month-to-date;
  // otherwise (a past academic year has no "current month" within it) fall
  // back to that year's own full range.
  useEffect(() => {
    if (!selectedYear) return;
    const yearStart = selectedYear.startDate.slice(0, 10);
    const yearEnd = selectedYear.endDate.slice(0, 10);
    const monthStart = dayjs().startOf('month').format('YYYY-MM-DD');
    const today = dayjs().format('YYYY-MM-DD');
    if (monthStart >= yearStart && today <= yearEnd) {
      setStartDate(monthStart);
      setEndDate(today);
    } else {
      setStartDate(yearStart);
      setEndDate(yearEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear?.id]);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: pageData, isFetching } = useQuery({
    queryKey: ['attendance', 'students-summary', dt.params, effectiveYearId, classId, sectionId, startDate, endDate],
    queryFn: () =>
      AttendanceApi.studentsSummary({
        ...dt.params,
        academicYearId: effectiveYearId || undefined,
        classId: classId || undefined,
        sectionId: sectionId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !yearLoading && !!startDate && !!endDate,
    placeholderData: keepPreviousData,
  });

  const columns = [
    columnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    columnHelper.accessor('admissionNo', { header: 'Admission No' }),
    columnHelper.accessor('rollNumber', { header: 'Roll No', cell: (ctx) => ctx.getValue() ?? '—' }),
    columnHelper.accessor('studentName', { header: 'Name' }),
    columnHelper.accessor('present', {
      header: 'Present',
      cell: (ctx) => <Badge tone="green">{ctx.getValue()}</Badge>,
    }),
    columnHelper.accessor('absent', {
      header: 'Absent',
      cell: (ctx) => <Badge tone="red">{ctx.getValue()}</Badge>,
    }),
    columnHelper.accessor('workingDays', {
      header: 'Working Days',
      cell: (ctx) => <Badge tone="slate">{ctx.getValue()}</Badge>,
    }),
  ];

  return (
    <>
      <PageHeader
        title="Attendance Summary"
        description={`Present/absent totals per ${term.level.toLowerCase()} student over a date range.`}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={dt.searchInput}
            onChange={(e) => dt.setSearchInput(e.target.value)}
            placeholder="Search by name or admission #"
            className="pl-9"
          />
        </div>
        <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={classId}
          onChange={(e) => {
            setClassId(e.target.value);
            setSectionId('');
          }}
        >
          <option value="">All {term.level}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-40"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
          disabled={!classId}
        >
          <option value="">All {term.group}</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {!hasAnyData && !yearLoading ? (
        <div className="card p-8 text-center text-slate-500">
          No attendance recorded for the selected filters.
        </div>
      ) : (
        <DataTable
          columns={columns}
          result={pageData}
          state={dt}
          loading={isFetching}
          emptyMessage="No attendance recorded for the selected filters."
          getRowId={(row) => row.studentId}
        />
      )}
    </>
  );
}
